import { memo } from 'react';
import { Pencil, Unlink } from 'lucide-react';
import {
  Badge,
  IconButton,
  Kbd,
  SegmentedControl,
  WeightGlyph,
} from '@/components/ui';
import { formatCoordinate, formatResidual } from '@/logic/statistics';
import {
  getWeightStatus,
  hasResidual,
  WEIGHT,
  WEIGHT_OPTIONS,
  type WeightStatus,
} from '@/logic/weights';
import type { PointPair, PointResidual } from '@/types';
import { unitSuffix, WEIGHT_SHORTCUTS } from './mapDisplay';

const WEIGHT_SEGMENTS = WEIGHT_OPTIONS.map((option) => ({
  value: option.status,
  label: option.label,
  icon: <WeightGlyph status={option.status} />,
}));

const Coordinate = ({ axis, value }: { axis: string; value: string }) => (
  <span className="whitespace-nowrap font-mono tabular-nums">
    <span className="text-fg-muted">{axis} </span>
    {value}
  </span>
);

interface SelectedPairCardProps {
  pair: PointPair;
  residual: PointResidual | undefined;
  flagged: boolean;
  units?: string;
  onSetWeight: (id: number, weight: number) => void;
  onEditPair?: (id: number) => void;
  onRemovePair?: (id: number) => void;
}

/** The selected pair's coordinates, residual, weight, and actions. */
export const SelectedPairCard = memo(function SelectedPairCard({
  pair,
  residual,
  flagged,
  units,
  onSetWeight,
  onEditPair,
  onRemovePair,
}: SelectedPairCardProps) {
  const status = getWeightStatus(pair.weight);
  const suffix = unitSuffix(units);
  const showResidual =
    !!residual && residual.total !== null && hasResidual(pair.weight);

  return (
    <section
      aria-label="Selected pair"
      className="rounded-lg border border-border bg-surface-sunken p-3"
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium uppercase tracking-wide text-fg-muted">
            Selected pair
          </p>
          <h4 className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-fg">
            <span className="truncate">
              Record <span className="font-mono">{pair.sys1Point}</span> → Field{' '}
              <span className="font-mono">{pair.sys2Point}</span>
            </span>
            {flagged && (
              <Badge tone="danger" dot>
                Flagged for review
              </Badge>
            )}
          </h4>
        </div>
        <div className="-mr-1 -mt-1 flex shrink-0 items-center">
          {onEditPair && (
            <IconButton
              aria-label="Edit coordinates or match"
              size="sm"
              icon={<Pencil />}
              onClick={() => onEditPair(pair.id)}
            />
          )}
          {onRemovePair && (
            <IconButton
              aria-label="Unlink pair"
              size="sm"
              icon={<Unlink />}
              onClick={() => onRemovePair(pair.id)}
            />
          )}
        </div>
      </div>

      <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <span className="text-fg-muted">Record</span>
        <span className="flex flex-wrap gap-x-3">
          <Coordinate axis="N" value={formatCoordinate(pair.n1)} />
          <Coordinate axis="E" value={formatCoordinate(pair.e1)} />
        </span>
        <span className="text-fg-muted">Field</span>
        <span className="flex flex-wrap gap-x-3">
          <Coordinate axis="N" value={formatCoordinate(pair.n2)} />
          <Coordinate axis="E" value={formatCoordinate(pair.e2)} />
        </span>
        {showResidual && (
          <>
            <span className="text-fg-muted">Residual</span>
            <span className="flex flex-wrap gap-x-3">
              <span className="whitespace-nowrap font-mono font-medium tabular-nums text-fg">
                {formatCoordinate(residual.total)}
                {suffix && <span className="text-fg-muted"> {suffix}</span>}
              </span>
              <Coordinate axis="vN" value={formatResidual(residual.vn, 4)} />
              <Coordinate axis="vE" value={formatResidual(residual.ve, 4)} />
            </span>
          </>
        )}
      </div>

      <div className="mt-3 space-y-1.5">
        <SegmentedControl<WeightStatus>
          aria-label="Weight"
          size="sm"
          fullWidth
          value={status}
          onValueChange={(next) => onSetWeight(pair.id, WEIGHT[next])}
          options={WEIGHT_SEGMENTS}
        />
        <p className="flex flex-wrap items-center gap-1 text-xs text-fg-muted">
          Keys with the map focused:
          {WEIGHT_OPTIONS.map((option) => (
            <Kbd key={option.status} title={option.label}>
              {WEIGHT_SHORTCUTS[option.status]}
            </Kbd>
          ))}
        </p>
      </div>
    </section>
  );
});
